import * as React from 'react';
import {Provider} from 'react-redux';
import {Route, BrowserRouter as Router} from 'react-router-dom';
import {
  applyMiddleware,
  combineReducers,
  compose,
  createStore,
  StoreEnhancer
} from 'redux';
import thunkMiddleware from 'redux-thunk';
import App from './layout-components/App';
import ProductWrapper from './layout-components/ProductWrapper';
import AddressPage from './page-components/AddressPage';
import * as reducers from './reducers';
import ClientContextProvider, {
  ClientContextConsumer,
  IClientContext
} from './context/ClientContextProvider';
import {
  IThemeContext,
  ThemeConsumer,
  ThemeProvider
} from './context/ThemeContext';
import {Helmet} from 'react-helmet';
import {Sprite} from './__generated__/sprite.symbol';
import {SpidContextProvider} from '@snoam/mono-spid';
import {trimmedBasename, isProduction} from './fastflowUtils';
import ResponsiveContextProvider from './context/ResponsiveContext';
import {
  AsyncAcceptAgreementComponent,
  AsyncAddressPageComponent,
  AsyncCheckCodeComponent,
  AsyncCompanyPaysPageComponent,
  AsyncGotoSpidComponent,
  AsyncPlaceOrderComponent,
  AsyncProductComponent,
  AsyncProductQuarantinedComponent,
  AsyncReceiptPageComponent,
  AsyncSelectPaymentMethodComponent,
  AsyncStartPageComponent,
  AsyncUpgradePageComponent,
  AsyncUserInfoComponent,
  AsyncUserInfoLandingPageComponent,
  AsyncUserSearchComponent,
  AsyncWaitForEmailComponent
} from './page-components/AyncComponentLoader';
import { setCoreHeaderSessionAdapter } from "@snoam/mono-core-header-session-adapter";

const windowAsAny: any = window;
const devToolsExtension: StoreEnhancer = windowAsAny.__REDUX_DEVTOOLS_EXTENSION__
  ? windowAsAny.__REDUX_DEVTOOLS_EXTENSION__()
  : (f: any) => f;
const store = compose(
  applyMiddleware(thunkMiddleware),
  devToolsExtension
)(createStore)(
  combineReducers({
    ...reducers
  })
);

const basePath = (window.location.pathname.match(/.*\/ff\//) || ['/'])[0];
const domain = window.location.hostname;
const formIdFromUrl = window.location.pathname.replace(/\D+/g, '');
const isLocal = process.env.NODE_ENV == 'development';

if (typeof window != "undefined") {
  const isProd = isProduction(window.location);
  setCoreHeaderSessionAdapter(isProd, window.location.href);
}

export default (
  <Provider store={store}>
    <ClientContextProvider href={window.location.href} domain={domain}>
      <SpidContextProvider
        domain={domain}
        enableRefreshToken={false}
        redirectPath={`${trimmedBasename()}/products/${formIdFromUrl}`}
        href={window.location.href}
        siteSpecificLogout={!isLocal}
      >
        <ClientContextConsumer>
          {(clientContext: IClientContext) => (
            <React.Fragment>
              {!(window as any).ISSSR && (
                <ThemeProvider
                  clientShortHandName={clientContext.clientShortHandName}
                >
                  <ThemeConsumer>
                    {(theme: IThemeContext) => (
                      <Helmet>
                        <link
                          href={`${basePath}assets/css/${theme.themeCss}`}
                          rel="stylesheet"
                        />
                      </Helmet>
                    )}
                  </ThemeConsumer>
                </ThemeProvider>
              )}
              <Router basename={basePath}>
                <Route path="/products/:productId">
                  <ResponsiveContextProvider>
                    <App
                      clientName={clientContext.clientName}
                      clientShortHandName={clientContext.clientShortHandName}
                      hostname={clientContext.hostname}
                    >
                      <ProductWrapper hostname={clientContext.hostname}>
                        <Route
                          exact={true}
                          path="/products/:productId/"
                          render={props => (
                            <AsyncProductComponent {...props} key={1}/>
                          )}
                        />
                        <Route
                          path="/products/:productId/start"
                          render={props => (
                            <AsyncStartPageComponent {...props} key={2}/>
                          )}
                        />
                        <Route
                          path="/products/:productId/selectpaymentmethod/:paymentMethodId"
                          render={props => (
                            <AsyncSelectPaymentMethodComponent
                              {...props}
                              key={3}
                            />
                          )}
                        />
                        <Route
                          exact={true}
                          path="/products/:productId/selectpaymentmethod"
                          render={props => (
                            <AsyncSelectPaymentMethodComponent
                              {...props}
                              key={4}
                            />
                          )}
                        />
                        <Route
                          path="/products/:productId/usersearch"
                          render={props => (
                            <AsyncUserSearchComponent {...props} key={5}/>
                          )}
                        />
                        <Route
                          path="/products/:productId/checkcode"
                          render={props => (
                            <AsyncCheckCodeComponent {...props} key={6}/>
                          )}
                        />
                        <Route
                          path="/products/:productId/userinfo"
                          render={props => (
                            <AsyncUserInfoComponent {...props} key={7}/>
                          )}
                        />
                        <Route
                          path="/products/:productId/address"
                          component={AddressPage}
                          render={props => (
                            <AsyncAddressPageComponent {...props} key={8}/>
                          )}
                        />
                        <Route
                          path="/products/:productId/waitForEmail"
                          render={props => (
                            <AsyncWaitForEmailComponent {...props} key={9}/>
                          )}
                        />
                        <Route
                          path="/products/:productId/userinfolandingpage"
                          render={props => (
                            <AsyncUserInfoLandingPageComponent
                              {...props}
                              key={10}
                            />
                          )}
                        />
                        <Route
                          path="/products/:productId/placeorder"
                          render={props => (
                            <AsyncPlaceOrderComponent {...props} key={11}/>
                          )}
                        />
                        <Route
                          path="/products/:productId/productquarantined"
                          render={props => (
                            <AsyncProductQuarantinedComponent
                              {...props}
                              key={12}
                            />
                          )}
                        />
                        <Route
                          path="/products/:productId/receipt"
                          render={props => (
                            <AsyncReceiptPageComponent {...props} key={13}/>
                          )}
                        />
                        <Route
                          path="/products/:productId/gotospid"
                          render={props => (
                            <AsyncGotoSpidComponent {...props} key={14}/>
                          )}
                        />
                        <Route
                          path="/products/:productId/acceptagreement"
                          render={props => (
                            <AsyncAcceptAgreementComponent
                              {...props}
                              key={15}
                            />
                          )}
                        />
                        <Route
                          path="/products/:productId/companypays"
                          render={props => (
                            <AsyncCompanyPaysPageComponent
                              {...props}
                              key={16}
                            />
                          )}
                        />
                        <Route
                          path="/products/:productId/upgrade"
                          render={props => (
                            <AsyncUpgradePageComponent {...props} key={17}/>
                          )}
                        />
                      </ProductWrapper>
                    </App>
                  </ResponsiveContextProvider>
                </Route>
              </Router>
            </React.Fragment>
          )}
        </ClientContextConsumer>
        <Sprite/>
      </SpidContextProvider>
    </ClientContextProvider>
  </Provider>
);
